import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LockedPage from '../components/LockedPage';
import useClientSearch from '../hooks/useClientSearch';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatPrice(p) {
  if (!p && p !== 0) return '—';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p);
}

function Toast({ message, type }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl text-white text-sm font-medium animate-scale-in ${type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
      {message}
    </div>
  );
}

function EnrichmentBadge({ status }) {
  if (!status) return null;
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">
      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
      Verrijking bezig…
    </span>
  );
  if (status === 'done') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">
      ✓ Verrijkt
    </span>
  );
  if (status === 'failed') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-600 font-medium">
      ⚠ Verrijking mislukt
    </span>
  );
  return null;
}

function Tag({ children, color = 'blue' }) {
  const cls = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
    amber: 'bg-amber-50 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
  }[color] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{children}</span>;
}

function EnrichmentPanel({ enrichment, booking }) {
  if (!enrichment || enrichment.enrichment_status !== 'done') {
    return (
      <div className="px-6 py-5 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-400">
        {enrichment?.enrichment_status === 'pending' ? (
          <div className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            Claude zoekt aanvullende bestemmingsinformatie op het web…
          </div>
        ) : enrichment?.enrichment_status === 'failed' ? (
          'Automatische verrijking mislukt.'
        ) : 'Geen verrijkingsdata beschikbaar.'}
      </div>
    );
  }
  const e = enrichment;
  const depMonth = booking?.departure_date
    ? new Date(booking.departure_date).toLocaleDateString('nl-NL', { month: 'long' })
    : '';

  return (
    <div className="border-t border-gray-100 bg-gray-50">
      <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

        {/* Hotel */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Hotel</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {e.hotel_stars && <span className="text-amber-400 text-sm">{'⭐'.repeat(Math.min(e.hotel_stars, 5))}</span>}
              {e.hotel_review_score && <span className="text-sm font-bold text-gray-700">{e.hotel_review_score}</span>}
            </div>
            {e.hotel_beach_distance && (
              <p className="text-xs text-gray-600">{e.hotel_beach_distance}</p>
            )}
            {e.hotel_description && (
              <p className="text-xs text-gray-600 leading-relaxed">{e.hotel_description}</p>
            )}
            {e.hotel_highlights?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {e.hotel_highlights.map((h, i) => <Tag key={i} color="blue">{h}</Tag>)}
              </div>
            )}
            {e.hotel_facilities?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {e.hotel_facilities.slice(0, 6).map((f, i) => <Tag key={i} color="gray">{f}</Tag>)}
                {e.hotel_facilities.length > 6 && <Tag color="gray">+{e.hotel_facilities.length - 6}</Tag>}
              </div>
            )}
          </div>
        </div>

        {/* Weather */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Weer {depMonth && `in ${depMonth}`}</p>
          <div className="space-y-1.5">
            {[
              { label: 'Temperatuur', value: e.avg_temp_celsius != null ? `${e.avg_temp_celsius}°C` : null },
              { label: 'Zonuren', value: e.avg_sun_hours != null ? `${e.avg_sun_hours} uur/dag` : null },
              { label: 'Zeewater', value: e.avg_sea_temp_celsius != null ? `${e.avg_sea_temp_celsius}°C` : null },
              { label: 'Neerslag', value: e.rainfall_mm != null ? `${e.rainfall_mm} mm` : null },
              { label: 'UV-index', value: e.uv_index_avg != null ? `${e.uv_index_avg}` : null },
            ].filter(r => r.value).map(({ label, value }) => (
              <div key={label} className="flex justify-between text-xs">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-700">{value}</span>
              </div>
            ))}
            {e.season_classification && (
              <div className="mt-1">
                <Tag color={e.season_classification === 'hoogseizoen' ? 'amber' : e.season_classification === 'naseizoen' ? 'green' : 'gray'}>
                  {e.season_classification}
                </Tag>
              </div>
            )}
            {e.local_events?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-400 mb-1">Evenementen:</p>
                {e.local_events.slice(0, 2).map((ev, i) => <p key={i} className="text-xs text-gray-600">• {ev}</p>)}
              </div>
            )}
          </div>
        </div>

        {/* Practical */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Praktisch</p>
          <div className="space-y-1.5">
            {[
              { label: 'Vluchtduur', value: e.flight_duration_minutes ? `${e.flight_duration_minutes} min` : null },
              { label: 'Tijdzone', value: e.timezone_offset },
              { label: 'Visum', value: e.visa_required != null ? (e.visa_required ? `Ja${e.visa_notes ? ': ' + e.visa_notes : ''}` : 'Niet nodig') : null },
              { label: 'Vaccinaties', value: e.vaccination_advice },
              { label: 'Kraanwater', value: e.tap_water_safe != null ? (e.tap_water_safe ? 'Veilig' : 'Niet veilig') : null },
              { label: 'Valuta', value: e.currency ? `${e.currency}${e.currency_rate_to_eur ? ' · ' + e.currency_rate_to_eur : ''}` : null },
            ].filter(r => r.value).map(({ label, value }) => (
              <div key={label} className="text-xs">
                <span className="text-gray-400">{label}:</span>
                <span className="ml-1 text-gray-700">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upsell */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Upsell</p>
          {e.popular_excursions?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-1">Excursies:</p>
              <div className="flex flex-wrap gap-1">
                {e.popular_excursions.slice(0, 5).map((ex, i) => <Tag key={i} color="purple">{ex}</Tag>)}
              </div>
            </div>
          )}
          {e.packing_tips?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-1">Inpaktips:</p>
              {e.packing_tips.slice(0, 3).map((t, i) => <p key={i} className="text-xs text-gray-600">• {t}</p>)}
            </div>
          )}
          {e.insurance_considerations && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Verzekering:</p>
              <p className="text-xs text-gray-600 leading-relaxed">{e.insurance_considerations}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DropZone({ onFile, uploading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type === 'application/pdf') onFile(file);
  };
  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`relative border border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${dragging ? 'border-blue-400 bg-blue-50 scale-[1.01]' : uploading ? 'border-gray-200 bg-gray-50 cursor-wait' : 'hover:border-blue-400 hover:bg-blue-50/50'}`}
    >
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          <p className="text-blue-600 font-semibold text-sm">PDF verwerken…</p>
          <p className="text-gray-400 text-xs">Gegevens worden geëxtraheerd · Verrijking start daarna automatisch</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <svg className={`w-5 h-5 transition-colors ${dragging ? 'text-blue-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <div>
            <p className="text-gray-700 text-sm font-medium">Klik om een PDF te uploaden</p>
            <p className="text-gray-400 text-xs mt-0.5">of sleep een bestand hierheen · max 25 MB</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Client Link Modal ────────────────────────────────────────────────────────

// Map cryptic API/Supabase errors to readable Dutch messages
function friendlyError(msg = '') {
  if (!msg) return 'Er is iets misgegaan';
  if (msg.toLowerCase().includes('pattern') || msg.toLowerCase().includes('format')) return 'Ongeldig e-mailadres formaat';
  if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('duplicate')) return 'Dit e-mailadres is al in gebruik';
  if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('niet gevonden')) return 'Boeking niet gevonden';
  return msg;
}

function ClientLinkModal({ booking, onClose, onLinked, authFetch }) {
  const leadName = Array.isArray(booking.traveler_names) && booking.traveler_names.length
    ? booking.traveler_names[0]
    : '';

  const { query, setQuery, results, searching, search, reset } = useClientSearch();
  const [mode, setMode] = useState('search'); // 'search' | 'create'
  const [selectedClient, setSelectedClient] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-populate search with the booking's lead name on mount
  useEffect(() => {
    if (leadName.length >= 2) {
      setQuery(leadName);
      search(leadName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    setSelectedClient(null);
    search(val);
  }

  async function handleLinkExisting(client) {
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(`/api/clients/${client.client_id}/link-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: booking.id }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(friendlyError(d.error) || 'Koppelen mislukt');
      }
      onLinked({ client_id: client.client_id, client_match_status: 'linked' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreate() {
    // Client-side email validation before hitting the API
    const trimmedEmail = newEmail.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Voer een geldig e-mailadres in');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: leadName,
          email: trimmedEmail || undefined,
          booking_id: booking.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(friendlyError(data.error));
      onLinked({ client_id: data.client.id, client_match_status: 'linked' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddEmail() {
    if (!selectedClient || !newEmail.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(`/api/clients/${selectedClient.client_id}/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail.trim(), booking_id: booking.id }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(friendlyError(d.error) || 'E-mail toevoegen mislukt');
      }
      // After adding email, link the booking
      await handleLinkExisting(selectedClient);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="font-semibold text-gray-900 text-sm">Koppel aan klant</p>
            {leadName && <p className="text-xs text-gray-500 mt-0.5">Boeking · {leadName}</p>}
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="px-6 py-5">

          {/* Mode toggle */}
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => { setMode('search'); setError(null); setSelectedClient(null); reset(); }}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${mode === 'search' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Bestaande klant
            </button>
            <button
              onClick={() => { setMode('create'); setError(null); setSelectedClient(null); reset(); }}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${mode === 'create' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Nieuwe klant
            </button>
          </div>

          {/* Search mode */}
          {mode === 'search' && (
            <div>
              <div className="relative mb-3">
                <input
                  type="text"
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="Zoek op naam of e-mail…"
                  className="w-full pl-4 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  autoFocus
                />
                {searching && (
                  <svg className="absolute right-3 top-3 w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                )}
              </div>

              {/* Results */}
              {results.length > 0 && !selectedClient && (
                <div className="border border-gray-100 rounded-lg overflow-hidden mb-3">
                  {results.map(c => (
                    <button
                      key={c.client_id}
                      onClick={() => setSelectedClient(c)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                        {c.full_name?.[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{c.full_name}</p>
                        {c.primary_email && <p className="text-xs text-gray-500 truncate">{c.primary_email}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected client — confirm or add email */}
              {selectedClient && (
                <div className="border border-blue-100 bg-blue-50 rounded-lg px-4 py-3 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                        {selectedClient.full_name?.[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{selectedClient.full_name}</p>
                        <p className="text-xs text-gray-500">{selectedClient.primary_email || 'Geen e-mail bekend'}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedClient(null)} className="text-xs text-gray-400 hover:text-gray-600">Wijzig</button>
                  </div>

                  {!selectedClient.primary_email && (
                    <div className="mb-3">
                      <p className="text-xs text-amber-700 mb-2">Deze klant heeft nog geen e-mailadres. Voeg er optioneel een toe:</p>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        placeholder="E-mailadres (optioneel)"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                  )}

                  <button
                    onClick={() => newEmail.trim() && !selectedClient.primary_email ? handleAddEmail() : handleLinkExisting(selectedClient)}
                    disabled={submitting}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {submitting ? 'Bezig…' : 'Koppelen'}
                  </button>
                </div>
              )}

              {!searching && query.length >= 2 && results.length === 0 && !selectedClient && (
                <p className="text-xs text-gray-400 text-center py-2">Geen klanten gevonden · <button onClick={() => { setMode('create'); reset(); }} className="text-blue-600 hover:underline">Nieuwe klant aanmaken</button></p>
              )}
            </div>
          )}

          {/* Create mode */}
          {mode === 'create' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Naam</label>
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">{leadName || '—'}</div>
                <p className="text-xs text-gray-400 mt-1">Overgenomen uit boeking</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">E-mailadres <span className="font-normal text-gray-400">(optioneel)</span></label>
                <input
                  type="text"
                  inputMode="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="klant@voorbeeld.nl"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                />
              </div>
              <button
                onClick={handleCreate}
                disabled={submitting || !leadName}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {submitting ? 'Bezig…' : 'Klant aanmaken & koppelen'}
              </button>
            </div>
          )}

          {error && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-4">
          <button onClick={onClose} className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Overslaan
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Column config ────────────────────────────────────────────────────────────

const COLS = [
  { key: 'traveler_names', label: 'Hoofdreiziger', render: v => Array.isArray(v) && v.length ? v[0] : '—' },
  { key: 'destination',    label: 'Bestemming',   render: v => v || '—' },
  { key: 'departure_date', label: 'Vertrek',       render: v => formatDate(v) },
  { key: 'duration_days',  label: 'Reisduur',      render: v => v ? `${v} dgn` : '—' },
  { key: 'hotel_name',     label: 'Hotel',         render: v => v || '—' },
  { key: 'board_type',     label: 'Verzorging',    render: v => v || '—' },
  { key: 'total_price',    label: 'Totaalprijs',   render: v => formatPrice(v) },
];

// ── Page ─────────────────────────────────────────────────────────────────────

function BookingsInner() {
  const { authFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [enrichments, setEnrichments] = useState({}); // bookingId → enrichment data
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expanded, setExpanded] = useState(null); // expanded booking id
  const [search, setSearch] = useState('');
  const [linkingBooking, setLinkingBooking] = useState(null); // booking to link
  const pollRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/bookings');
      const data = await res.json();
      if (res.ok) setBookings(data);
    } catch (_) {}
    setLoading(false);
  }, [authFetch]);

  // Fetch full enrichment for a booking
  const loadEnrichment = useCallback(async (bookingId) => {
    try {
      const res = await authFetch(`/api/enrich-booking/${bookingId}`);
      if (res.ok) {
        const data = await res.json();
        setEnrichments(prev => ({ ...prev, [bookingId]: data }));
      }
    } catch (_) {}
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  // Poll every 5s while any booking has enrichment_status === 'pending'
  useEffect(() => {
    const hasPending = bookings.some(b => b.enrichment_status === 'pending');
    if (hasPending) {
      pollRef.current = setInterval(load, 5000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [bookings, load]);

  // Load enrichment when a row is expanded
  useEffect(() => {
    if (expanded && !enrichments[expanded]) {
      loadEnrichment(expanded);
    }
  }, [expanded, enrichments, loadEnrichment]);

  const handleFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await authFetch('/api/bookings/upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload mislukt');
      await load();
      // Open client link modal for the newly uploaded booking
      setLinkingBooking(data.booking);
    } catch (err) {
      showToast(err.message, 'error');
    }
    setUploading(false);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await authFetch(`/api/bookings/${id}`, { method: 'DELETE' });
      setBookings(prev => prev.filter(b => b.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (_) { showToast('Verwijderen mislukt', 'error'); }
    setDeletingId(null);
  };

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  // Update local booking state after client is linked
  const handleLinked = useCallback(({ client_id, client_match_status }) => {
    setBookings(prev => prev.map(b =>
      b.id === linkingBooking?.id ? { ...b, client_id, client_match_status } : b
    ));
    setLinkingBooking(null);
    showToast('Klant gekoppeld');
  }, [linkingBooking]);

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (b.destination || '').toLowerCase().includes(q) ||
      (Array.isArray(b.traveler_names) && b.traveler_names.some(n => n.toLowerCase().includes(q))) ||
      (b.hotel_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page-enter">
      {toast && <Toast message={toast.message} type={toast.type} />}

      {linkingBooking && (
        <ClientLinkModal
          booking={linkingBooking}
          onClose={() => { setLinkingBooking(null); showToast('Boeking ingevoerd! Claude verrijkt nu de bestemmingsdata…'); }}
          onLinked={handleLinked}
          authFetch={authFetch}
        />
      )}

      {/* Header */}
      <div className="pro-page-header">
        <h1 className="text-xl font-semibold text-gray-900">Boekingen</h1>
        <p className="text-sm text-gray-500 mt-0.5">Upload boekings-PDF's — AI verrijkt automatisch met actuele bestemmingsdata</p>
      </div>

      <div className="p-8">

      {/* Upload zone */}
      <div className="pro-card p-5 mb-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-0.5">PDF importeren</h2>
        <p className="text-xs text-gray-500 mb-4">Regex-extractie + Claude AI-verrijking met webzoekfunctie</p>
        <DropZone onFile={handleFile} uploading={uploading} />
      </div>

      {/* Stats */}
      {bookings.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Totaal boekingen', value: bookings.length },
            { label: 'Unieke bestemmingen', value: new Set(bookings.map(b => b.destination).filter(Boolean)).size },
            { label: 'Totale omzet', value: formatPrice(bookings.reduce((s, b) => s + (b.total_price || 0), 0)) },
          ].map(({ label, value }) => (
            <div key={label} className="pro-stat-card">
              <p className="text-xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="pro-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-800">Boekingsoverzicht</h2>
            {bookings.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium">{bookings.length}</span>
            )}
          </div>
          {bookings.length > 0 && (
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Zoek reiziger, bestemming, hotel…"
              className="pl-4 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-64" />
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <svg className="w-8 h-8 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 px-4">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-600 font-medium text-sm">{search ? 'Geen boekingen gevonden' : 'Nog geen boekingen'}</p>
            <p className="text-gray-400 text-xs mt-1">{search ? 'Pas je zoekopdracht aan' : 'Upload een boekings-PDF hierboven'}</p>
          </div>
        ) : (
          <div>
            {filtered.map(b => (
              <div key={b.id} className="border-b border-gray-50 last:border-0">
                {/* Main row */}
                <div
                  onClick={() => toggleExpand(b.id)}
                  className={`flex items-center gap-2 hover:bg-blue-50/30 cursor-pointer transition-colors ${expanded === b.id ? 'bg-blue-50/20' : ''}`}
                >
                  {/* Expand chevron */}
                  <div className="pl-4 py-3.5 flex-shrink-0">
                    <svg className={`w-4 h-4 text-gray-300 transition-transform ${expanded === b.id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {/* Cells */}
                  {COLS.map(c => (
                    <div key={c.key} className="flex-1 px-3 py-3.5 text-sm text-gray-700 min-w-0">
                      {c.key === 'traveler_names' ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                              {(c.render(b[c.key]) || '?')[0]?.toUpperCase()}
                            </div>
                            <span className="font-medium truncate">{c.render(b[c.key])}</span>
                          </div>
                          <div className="ml-9 flex items-center gap-2 flex-wrap">
                            <EnrichmentBadge status={b.enrichment_status} />
                            {/* Client link status */}
                            {b.client_id ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 font-medium">
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>
                                Klant gekoppeld
                              </span>
                            ) : (
                              <button
                                onClick={e => { e.stopPropagation(); setLinkingBooking(b); }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 font-medium hover:bg-amber-100 transition-colors"
                              >
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>
                                Koppel klant
                              </button>
                            )}
                          </div>
                        </div>
                      ) : c.key === 'total_price' ? (
                        <span className="font-semibold text-green-600">{c.render(b[c.key])}</span>
                      ) : (
                        <span className="truncate block">{c.render(b[c.key])}</span>
                      )}
                    </div>
                  ))}

                  {/* Delete */}
                  <div className="pr-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button onClick={e => handleDelete(e, b.id)} disabled={deletingId === b.id}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50">
                      {deletingId === b.id
                        ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      }
                    </button>
                  </div>
                </div>

                {/* Expandable enrichment panel */}
                {expanded === b.id && (
                  <EnrichmentPanel enrichment={enrichments[b.id] || { enrichment_status: b.enrichment_status }} booking={b} />
                )}
              </div>
            ))}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="hidden" />
        )}
      </div>

      </div>{/* end p-8 */}
    </div>
  );
}

export default function Bookings() {
  const { hasAccess } = useSubscription();
  if (!hasAccess('bureau')) return <LockedPage featureName="Boekingen"><BookingsInner /></LockedPage>;
  return <BookingsInner />;
}
